// 排除 (the owner's veto), client side. PURE — no requests, no browser storage.
//
// Magnus 2026-09-15: 「i mean at the 衣架 map i can remove what to put on the rack. for the 做好了 i
// can also remove it.」 The plan is a PROPOSAL, so 不要這款 / 今天不做 have to work on every screen,
// and they have to STICK: both live in noke_restock_v2_exclusions, so the server's own
// `plan.excluded` + `meta.excluded_counts` are the only source of truth a screen reads. The
// sessionStorage set that used to back the 訂貨 checkbox is gone — it vanished on Magnus's phone and
// silently returned the style, and Adam never saw his decisions at all.
//
// The one thing kept locally is the OPTIMISTIC window: between the tap and the refetch the tile has
// to leave the screen, so `VetoState` holds what was just asked for and `applyVeto` renders the plan
// as if the server had already agreed. Every key is dropped again the moment the refetch lands (or
// the moment the POST fails), so nothing here can outlive one request.
import { sourcingBucket } from './sourcing';
import { listVariants, variantVetoKey } from './counterList';
import {
  DoneKind, MoveKind, RestockV2ExcludedLists, RestockV2ExcludedMove, RestockV2ExcludedStyle,
  RestockV2ExcludeBody, RestockV2Line, RestockV2ListExcludedVariant, RestockV2Move, RestockV2Plan,
  RestockV2Tile, RestockV2Today, RestockV2TodayTotals, RestockV2UnexcludeBody, SeasonKey,
} from './types';

// 今天不做 writes the kind the ORIGINAL move writes, so a skip and a 做好了 address the same row:
// the 到貨後 cards are bring_back, the 等到貨 receipts are 'received' (see TODAY_KIND in format.ts).
export const SKIP_KIND: Record<MoveKind, DoneKind> = {
  display: 'display', from_office: 'from_office', bring_back: 'bring_back',
  on_arrival: 'bring_back', waiting: 'received',
};

export const EXCLUDE_LABEL = '不要這款';
export const SKIP_LABEL = '今天不做';
export const ADD_BACK_LABEL = '加回';
// A plan-scope veto is the style, so the confirm line says what it costs: all three screens.
export const EXCLUDE_CONFIRM = '確定不要這款？三個畫面都不會再出現。';
export const EXPIRED_UNDO = '超過 12 小時，請到庫存頁調整';

// ---- 不要這款 vs 不要這個顏色／尺寸 ------------------------------------------------------------
// Magnus 2026-09-17, looking at a rack tile for 白色 S of 3127: 「i should be able to choose 不要這款
// or this specific color or size only」. The tile he taps IS one colour and one size, and the only
// verb it offered was the style. He lost 3127 — his best-selling counter garment, 11 pieces in 30
// days — meaning to drop one size.
//
// So anywhere the control sits on an element that names ONE variant, the confirm asks WHICH, with
// the narrow verb first: it is the recoverable one (a 加回 chip waits for it in 選品), and the wide
// one should cost a deliberate extra beat. On an element that names only a style, nothing changes.
export const EXCLUDE_VARIANT_LABEL = '不要這個顏色／尺寸';
export const EXCLUDE_CHOICE_TITLE = '不要哪一個？';
// Two consequences, two sentences. Sharing one line is what made the tile lie in the first place.
export const EXCLUDE_VARIANT_CONFIRM = '只拿掉這個顏色／尺寸，其他顏色照常；架上有的會排進帶回。';
export const EXCLUDE_STYLE_CONFIRM = '整款都不要，三個畫面都不會再出現。';

// ---- 帶回 是提案，不是命令 --------------------------------------------------------------------
// Magnus 2026-09-17: 「for the ones showing 帶回 allow me to remove so it doesn't show or refute
// making it stay.」 A 帶回 tile is the engine saying 「take this off the rail」 and he had no way to
// disagree — the tile's only control was 不要這款, which is agreeing HARDER.
//
// So a 帶回 tile gets two verbs, on the day/permanent split this codebase already uses everywhere
// else (今天不做 vs 不要這款, 略過 vs 排除):
//   今天不做  the existing scope='today' row. Gone overnight, costs nothing.
//   留著      NEW and permanent. The variant stays on the rail and the engine stops proposing 帶回
//             for it, until he takes it back with 取消留著.
// They are DIFFERENT commitments, so they get different sentences. One line describing both is the
// same lie the shared veto line was.
export const KEEP_LABEL = '留著';
export const UNKEEP_LABEL = '取消留著';
export const BRING_BACK_CHOICE_TITLE = '這件不帶回？';
// 今天不做 from a rack tile writes the SAME row the 今天 card's 今天不做 writes (scope='today',
// move_kind='bring_back'), so the two screens cannot disagree about what was skipped.
export const SKIP_BRING_BACK_CONFIRM = '只有今天不帶回，明天會再排進帶回清單。';
export const KEEP_BRING_BACK_CONFIRM = '一直留在架上，以後都不會再排帶回；件數照算進架上容量。';
export const UNKEEP_CONFIRM = '取消後，這件會重新排進帶回清單。';
// What a tile says about itself once he has decided. A decision he made is never hidden — it is
// marked on the tile AND listed in a fold, both with one tap back.
export const KEPT_CHIP = '留著';
export const SKIPPED_TODAY_CHIP = '今天不做';
export const UNSKIP_BRING_BACK_LABEL = '恢復帶回';

/** 「留著 3 件 · 查看」 — the fold that makes 留著 reversible from somewhere obvious. */
export const keptFoldLabel = (count: number): string => `${KEEP_LABEL} ${count} 件 · 查看`;

/** The scope='today' row a 帶回 TILE skips. Same key the matching 今天 card would write. */
export function tileSkipKey(sku: string, variantId?: number | null): string {
  return todaySkipKey('bring_back', sku, variantId);
}

/** The colour/size a control is sitting on, when it is sitting on one. */
export interface VetoVariant {
  variant_id: number;
  color: string | null;
  size: string | null;
}

/**
 * What every screen needs to raise a veto, as one prop instead of four. `onVariant` is offered only
 * where the element names a variant AND the table is migrated; everything else keeps today's
 * two-button dialog.
 */
export interface VetoControls {
  onStyle: (sku: string) => void;
  onVariant: (sku: string, variant: VetoVariant) => void;
  // The 帶回 tile's own verbs. Same optimistic store, same sku+variant key.
  onSkipBringBack: (sku: string, variant: VetoVariant) => void;
  onUnskipBringBack: (sku: string, variant: VetoVariant) => void;
  onKeep: (sku: string, variant: VetoVariant) => void;
  onUnkeep: (sku: string, variantId: number) => void;
  // 設為主打 / 取消主打. `mainPinned` folds the optimistic state over the served pin so the badge
  // moves on the tap, not ~6s later when the recompute lands — see pinnedVariantIds.
  onMain: (sku: string, variant: VetoVariant) => void;
  onUnmain: (sku: string, variant?: VetoVariant | null) => void;
  mainPinned: (
    sku: string, served: Array<number | string | null | undefined> | number | string | null | undefined,
  ) => string[];
  mainsReady: boolean;
  variantsReady: boolean;
  pending: Record<string, boolean>;
}

/** The variant a control sits on, or null when it names only a style (then: style verb only). */
export function vetoVariantOf(
  row: { variant_id?: number | null; color?: string | null; size?: string | null },
): VetoVariant | null {
  const id = row.variant_id;
  if (id === null || id === undefined) return null;
  return { variant_id: id, color: row.color ?? null, size: row.size ?? null };
}

/** One 今天 row of one move kind. A NULL variant means "every colour of that move". */
export function todaySkipKey(kind: DoneKind, sku: string, variantId?: number | null): string {
  return `${kind}|${sku}|${variantId === null || variantId === undefined ? '' : variantId}`;
}

export function moveSkipKey(move: RestockV2Move): string {
  return todaySkipKey(SKIP_KIND[move.kind], move.line.sku, move.line.variant_id);
}

export function excludedMoveKey(row: RestockV2ExcludedMove): string {
  return todaySkipKey(row.move_kind, row.sku, row.variant_id);
}

export const planVetoKey = (sku: string): string => `plan|${sku}`;

// What has been ASKED FOR but not yet confirmed by a recompute. `restored` is the 加回 side: the
// server still lists the row, so the fold hides it locally until the refetch drops it.
export interface VetoState {
  plan: Record<string, true>;
  today: Record<string, true>;
  // 不要這個顏色／尺寸, keyed by variantVetoKey(sku, variant_id) — NOT by SKU. Keying this half by
  // SKU like `plan` would make one size's veto blank every other colour of the style between the
  // tap and the refetch, which is the exact confusion this control exists to end.
  variant: Record<string, true>;
  // 留著 / 取消留著, keyed by variantVetoKey like `variant` — the pair, never the SKU. Two maps
  // rather than one boolean map because the two directions have different endpoints and a tile has
  // to be able to show 「pinned」 the instant he taps, whichever way he tapped.
  kept: Record<string, true>;
  unkept: Record<string, true>;
  restored: Record<string, true>;
  // 設為主打 / 取消主打 (Magnus 2026-09-19). `main` is keyed by variantVetoKey(sku, variant_id) like
  // the two above; `unmain` by variantVetoKey(sku, null) — i.e. by the STYLE — because clearing is
  // a per-style verb (the PRIMARY KEY on noke_counter_list_mains is the sku). One pin per style is
  // what makes the pair renderable optimistically: an entry in `main` IS the pin until a plan that
  // knows about it lands, whatever the served row says.
  main: Record<string, true>;
  unmain: Record<string, true>;
}

export const EMPTY_VETO: VetoState = { plan: {}, today: {}, variant: {}, kept: {}, unkept: {},
  restored: {}, main: {}, unmain: {} };

/** `mine` minus every key that was already in `landed`. */
function withoutKeys(mine?: Record<string, true>, landed?: Record<string, true>): Record<string, true> {
  const out: Record<string, true> = {};
  Object.keys(mine || {}).forEach((key) => { if (!(landed || {})[key]) out[key] = true; });
  return out;
}

/**
 * What is still in flight after a recompute lands: the current state MINUS the snapshot taken when
 * that recompute was REQUESTED.
 *
 * Magnus 2026-09-17: 「why i 加入清單 it goes back to before i add it?? why it reverted back」. The
 * screen used to drop the whole optimistic state on every landing, on the assumption that a landing
 * plan knows about everything answered before it. That is only true of answers given before the GET
 * was ISSUED. /restock-v2 takes ~6.3s and the debounce is 1.5s, so a ~6s window sat open after
 * almost every burst in which a plan computed BEFORE a tap would then erase it — the write had
 * succeeded, the row was in the database, and the screen drew a plan that predated the click.
 *
 * So a response may retire exactly what it could have known about, and nothing else.
 */
export function dropVetoState(current: VetoState, landed: VetoState): VetoState {
  return {
    plan: withoutKeys(current.plan, landed.plan),
    today: withoutKeys(current.today, landed.today),
    variant: withoutKeys(current.variant, landed.variant),
    kept: withoutKeys(current.kept, landed.kept),
    unkept: withoutKeys(current.unkept, landed.unkept),
    restored: withoutKeys(current.restored, landed.restored),
    main: withoutKeys(current.main, landed.main),
    unmain: withoutKeys(current.unmain, landed.unmain),
  };
}

/**
 * The variant id his 主色 points at RIGHT NOW — what he just tapped if anything is in flight,
 * otherwise what the server served. Returns null when there is no pin.
 *
 * The optimistic half wins on purpose: the plan takes ~6s to recompute and a badge that only
 * appears after it has landed reads as 「the tap did nothing」. `unmain` is checked FIRST so
 * clearing is visible instantly too, and a re-pin inside the same window drops the clear (useVeto
 * passes them as each other's `opposite`).
 */
export function pinnedVariantIds(
  sku: string, served: Array<number | string | null | undefined> | number | string | null | undefined,
  state?: VetoState | null,
): string[] {
  // A style may hold SEVERAL 主打 since 2026-09-20, so this returns the set rather than the one.
  const base = (Array.isArray(served) ? served : [served])
    .filter((id) => id !== null && id !== undefined && id !== '').map(String);
  const out = new Set(base);
  // 取消主打 first, so clearing is visible on the tap. A per-style clear (no variant in the key)
  // still means 「all of them」, which is what the verb meant while a style held only one.
  const cleared = Object.keys(state?.unmain || {}).filter((key) => key.startsWith(`${sku}|`));
  for (const key of cleared) {
    const id = key.slice(sku.length + 1);
    if (!id) out.clear(); else out.delete(id);
  }
  for (const key of Object.keys(state?.main || {})) {
    if (!key.startsWith(`${sku}|`)) continue;
    const id = key.slice(sku.length + 1);
    if (id) out.add(id);
  }
  const ids: string[] = [];
  out.forEach((id) => ids.push(id));
  return ids;
}


/** The same subtraction for the plain `id → true` maps the tab keeps (做好了 ticks). */
export function dropDecidedRows<T>(
  current: Record<string, T>, landed: Record<string, unknown>,
): Record<string, T> {
  const out: Record<string, T> = {};
  Object.keys(current || {}).forEach((key) => { if (!(landed || {})[key]) out[key] = current[key]; });
  return out;
}

export function vetoIsEmpty(state: VetoState): boolean {
  return !Object.keys(state.plan).length && !Object.keys(state.today).length
    && !Object.keys(state.variant || {}).length && !Object.keys(state.kept || {}).length
    && !Object.keys(state.unkept || {}).length && !Object.keys(state.restored).length
    && !Object.keys(state.main || {}).length && !Object.keys(state.unmain || {}).length;
}

// ---- reading the server's own lists ----------------------------------------------------------

export function excludedStyles(plan: RestockV2Plan | null | undefined): RestockV2ExcludedStyle[] {
  return plan?.excluded?.plan || [];
}

export function excludedMoves(plan: RestockV2Plan | null | undefined): RestockV2ExcludedMove[] {
  return plan?.excluded?.today || [];
}

/** `meta.excluded_counts` when the backend ships it, otherwise counted off the lists themselves. */
export function excludedCounts(plan: RestockV2Plan | null | undefined): { plan: number; today: number } {
  const counts = plan?.meta?.excluded_counts;
  return {
    plan: Number.isFinite(Number(counts?.plan)) ? Number(counts?.plan) : excludedStyles(plan).length,
    today: Number.isFinite(Number(counts?.today)) ? Number(counts?.today) : excludedMoves(plan).length,
  };
}

// A vetoed style has no rack of its own any more (it left the plan), so the 衣架 fold buckets it by
// the same category→fixture map the sourcing links use. A style with no category falls back with
// sourcingBucket's own default rather than disappearing.
export function rackOfExcluded(row: RestockV2ExcludedStyle): string {
  return sourcingBucket(row.cat);
}

/**
 * The 留著 fold's rows for ONE rack, so a decision he made is always reversible from somewhere
 * obvious — the same 「nothing vanishes silently」 contract 已排除 has.
 *
 * Two sources, in this order:
 *   1. the rack's OWN tiles with `kept === true`. These are optimistic for free (applyVeto pins
 *      them the instant he taps), which is why they come first and win on a dedupe.
 *   2. `list.kept_variants` rows bucketed onto this rack — a variant he pinned whose style has
 *      since left the rail has no tile, and without this it would be pinned with no way back.
 */
export function keptRowsForRack(
  tiles: RestockV2Tile[], offRail: RestockV2ListExcludedVariant[],
): RestockV2ListExcludedVariant[] {
  const rows: RestockV2ListExcludedVariant[] = [];
  const seen: Record<string, true> = {};
  tiles.forEach((tile) => {
    const id = tile.variant_id;
    if (tile.kept !== true || id === null || id === undefined) return;
    const key = variantVetoKey(tile.sku, id);
    if (seen[key]) return;
    seen[key] = true;
    rows.push({
      sku: tile.sku, variant_id: id, color: tile.color ?? null, size: tile.size ?? null,
      title: tile.title, image_url: tile.image_url ?? null, cat: null, season: tile.season ?? null,
      on_hand: tile.on_hand ?? null,
    });
  });
  offRail.forEach((row) => {
    const key = variantVetoKey(row.sku, row.variant_id);
    if (seen[key]) return;
    seen[key] = true;
    rows.push(row);
  });
  return rows;
}

/** 留著 rows grouped onto racks the same way 已排除 styles are — by the style's category. */
export function keptByRack(
  rows: RestockV2ListExcludedVariant[],
): Record<string, RestockV2ListExcludedVariant[]> {
  const out: Record<string, RestockV2ListExcludedVariant[]> = {};
  rows.forEach((row) => {
    const id = sourcingBucket(row.cat);
    out[id] = (out[id] || []).concat(row);
  });
  return out;
}

export function excludedByRack(rows: RestockV2ExcludedStyle[]): Record<string, RestockV2ExcludedStyle[]> {
  const out: Record<string, RestockV2ExcludedStyle[]> = {};
  rows.forEach((row) => {
    const id = rackOfExcluded(row);
    out[id] = (out[id] || []).concat(row);
  });
  return out;
}

/**
 * 「Magnus 9/15 排除」 — who took it out and when. Column-name agnostic on purpose: a style veto
 * carries created_by / created_at, a 不要這個顏色／尺寸 carries decided_by / decided_at, and both
 * rows sit in the same 已排除 fold, so they must read in the same voice.
 */
export function decidedByline(who?: string | null, at?: string | null): string {
  const when = at ? new Date(at) : null;
  const day = when && Number.isFinite(when.getTime()) ? `${when.getMonth() + 1}/${when.getDate()}` : '';
  const actor = (who || '').trim();
  if (actor && day) return `${actor} ${day} 排除`;
  if (actor) return `${actor} 排除`;
  return day ? `${day} 排除` : '已排除';
}

export function vetoByline(row: RestockV2ExcludedStyle): string {
  return decidedByline(row.created_by, row.created_at);
}

// ---- the request bodies (exactly what the routes accept) --------------------------------------

export function excludeStyleBody(clientRef: string, sku: string, reason?: string): RestockV2ExcludeBody {
  const body: RestockV2ExcludeBody = { client_ref: clientRef, sku, scope: 'plan' };
  if (reason) body.reason = reason;
  return body;
}

export function skipTodayBody(
  clientRef: string, sku: string, variantId: number | null, moveKind: DoneKind,
): RestockV2ExcludeBody {
  return { client_ref: clientRef, sku, scope: 'today', variant_id: variantId, move_kind: moveKind };
}

export function unexcludeStyleBody(clientRef: string, sku: string): RestockV2UnexcludeBody {
  return { client_ref: clientRef, sku, scope: 'plan' };
}

export function unskipTodayBody(
  clientRef: string, sku: string, variantId: number | null, moveKind: DoneKind,
): RestockV2UnexcludeBody {
  return { client_ref: clientRef, sku, scope: 'today', variant_id: variantId, move_kind: moveKind };
}

// ---- the optimistic view ---------------------------------------------------------------------

interface StyleFacts {
  title: string | null;
  image_url: string | null;
  cat: string | null;
  season: SeasonKey | null;
}

/** Whatever this plan still knows about a SKU, so a just-vetoed style can be named in the fold. */
function styleFactsOf(plan: RestockV2Plan, sku: string): StyleFacts {
  const facts: StyleFacts = { title: null, image_url: null, cat: null, season: null };
  const takeLine = (row: { title?: string | null; image_url?: string | null; cat?: string | null; season?: SeasonKey | null }) => {
    if (!facts.title && row.title) facts.title = row.title;
    if (!facts.image_url && row.image_url) facts.image_url = row.image_url;
    if (!facts.cat && row.cat) facts.cat = row.cat;
    if (!facts.season && row.season) facts.season = row.season;
  };
  const order = plan.order;
  [...(order?.groups || []).flatMap((group) => group.lines || []), ...(order?.warehouse || [])]
    .filter((line) => line.sku === sku).forEach(takeLine);
  Object.values(order?.tiers || {}).forEach((lines) => (lines || [])
    .filter((line) => line.sku === sku).forEach(takeLine));
  todayLists(plan.today).forEach((lines) => lines.filter((line) => line.sku === sku).forEach(takeLine));
  (plan.racks?.fixtures || []).forEach((fixture) => {
    [...(fixture.tiles || []), ...(fixture.keep || []), ...(fixture.add || []), ...(fixture.remove || [])]
      .filter((item) => item.sku === sku).forEach(takeLine);
  });
  return facts;
}

function todayLists(today: RestockV2Today | undefined): RestockV2Line[][] {
  return [today?.display || [], today?.from_office || [], today?.bring_back || [], today?.waiting || []];
}

const qty = (lines: RestockV2Line[]): number => lines.reduce((sum, line) => sum + (Number(line.qty) || 0), 0);

function todayTotals(lists: RestockV2Today): RestockV2TodayTotals {
  const phase = (name: 'now' | 'on_arrival') => qty(lists.bring_back.filter((line) => (line.phase || 'now') === name));
  return {
    display: qty(lists.display), from_office: qty(lists.from_office), bring_back: qty(lists.bring_back),
    waiting: qty(lists.waiting), bring_back_now: phase('now'), bring_back_on_arrival: phase('on_arrival'),
  };
}

/**
 * The plan as it will look once the server agrees: vetoed styles gone from the rack tiles and the
 * walk, today-skips gone from their own move, and both listed in `excluded` so the folds can show
 * them. The 訂貨 lines are deliberately NOT filtered here — that screen dims a line it is about to
 * lose (its existing affordance) and its own arithmetic drops it from the totals, the copied text
 * and the /ordered payload.
 *
 * `now_pieces` / `next_pieces` are left alone: that arithmetic belongs to the engine and the refetch
 * is one request away. Showing a stale bar for a second is honest; inventing one is not.
 */
export function applyVeto(plan: RestockV2Plan | null, state: VetoState): RestockV2Plan | null {
  if (!plan || vetoIsEmpty(state)) return plan;
  const vetoed = state.plan;
  const skipped = state.today;
  const deadVariants = state.variant || {};
  // Matched on the PAIR, and only when the row names a variant: a row with no variant_id is a
  // style row and can never be hit by a colour veto.
  const variantGone = (sku: string, variantId?: number | null) => (
    variantId !== null && variantId !== undefined && !!deadVariants[variantVetoKey(sku, variantId)]
  );
  const pinned = state.kept || {};
  const unpinned = state.unkept || {};

  /**
   * A rack tile as it looks the INSTANT he taps 留著 / 今天不做 on it, before the recompute lands.
   *
   * 留著 flips a 帶回 tile to ✓ and pins it — that is the whole point of the verb, and waiting
   * ~1.5s to see it would make the tap feel ignored. 今天不做 does NOT flip the badge: the piece
   * still has to come off eventually, so the tile keeps saying 帶回 and gains a 今天不做 mark.
   * Neither ever REMOVES the tile: 「never hide a decision he made」.
   *
   * `qty` is left alone. It is the engine's number and the engine owns it; the 數字待重算 bar is
   * already telling him the aggregates are pending.
   */
  const decided = (tile: RestockV2Tile): RestockV2Tile => {
    const id = tile.variant_id;
    if (id === null || id === undefined) return tile;
    const key = variantVetoKey(tile.sku, id);
    const skipKey = tileSkipKey(tile.sku, id);
    let next = tile;
    // A vetoed variant is never pinned: the two are one decision row on the server and 不要 wins.
    const dead = tile.vetoed === true || variantGone(tile.sku, id);
    if (pinned[key] && !dead) next = { ...next, kept: true, state: (next.state || 'ok') === 'remove' ? 'ok' : next.state };
    else if (unpinned[key]) next = { ...next, kept: false };
    if (skipped[skipKey]) next = { ...next, skipped_today: true };
    else if (state.restored[skipKey]) next = { ...next, skipped_today: false };
    return next;
  };
  const hiddenStyles: RestockV2ExcludedStyle[] = [];
  const hiddenMoves: RestockV2ExcludedMove[] = [];

  Object.keys(vetoed).forEach((sku) => {
    const facts = styleFactsOf(plan, sku);
    hiddenStyles.push({
      sku, title: facts.title, image_url: facts.image_url, cat: facts.cat, season: facts.season,
      reason: null, created_by: null, created_at: null, note: null,
    });
  });

  const keep = (kind: DoneKind) => (line: RestockV2Line) => {
    if (vetoed[line.sku]) return false;
    // A just-vetoed colour leaves its own 上架／去拿 row at once. It is NOT listed in the 今天略過
    // fold: that fold is 今天不做, a one-day skip, and this is a standing decision that belongs in
    // 選品's 已排除 — claiming it is a day-skip would offer the wrong 加回.
    if (variantGone(line.sku, line.variant_id)) return false;
    const exact = todaySkipKey(kind, line.sku, line.variant_id);
    const style = todaySkipKey(kind, line.sku, null);
    if (!skipped[exact] && !skipped[style]) return true;
    hiddenMoves.push({
      sku: line.sku, variant_id: line.variant_id, color: line.color, size: line.size,
      move_kind: kind, title: line.title, image_url: line.image_url ?? null,
    });
    return false;
  };
  const today: RestockV2Today = {
    display: (plan.today?.display || []).filter(keep('display')),
    from_office: (plan.today?.from_office || []).filter(keep('from_office')),
    bring_back: (plan.today?.bring_back || []).filter(keep('bring_back')),
    waiting: (plan.today?.waiting || []).filter(keep('received')),
    totals: plan.today?.totals || { display: 0, from_office: 0, bring_back: 0, waiting: 0 },
  };
  today.totals = todayTotals(today);

  // 選品's own surfaces move in the same frame: the chip flips to struck-with-加回 (or back), and the
  // 已排除 fold's variant half gains or loses its row. Counts and depth are NOT touched — the engine
  // owns those and they are marked 數字待重算 until it answers.
  const list = plan.list;
  const patchedList = list ? {
    ...list,
    approved: (list.approved || []).map((row) => {
      const variants = listVariants(row);
      if (!variants.length) return row;
      const flipped = variants.map((variant) => {
        if (variantGone(row.sku, variant.variant_id)) return { ...variant, vetoed: true };
        if (state.restored[variantVetoKey(row.sku, variant.variant_id)]) return { ...variant, vetoed: false };
        return variant;
      });
      return {
        ...row,
        variants: flipped.filter((variant) => variant.vetoed !== true),
        vetoed_variants: flipped.filter((variant) => variant.vetoed === true),
      };
    }),
    excluded_variants: (list.excluded_variants || [])
      .filter((row) => !state.restored[variantVetoKey(row.sku, row.variant_id)])
      .concat((list.approved || []).flatMap((row) => listVariants(row)
        .filter((variant) => variant.vetoed !== true && variantGone(row.sku, variant.variant_id))
        .map((variant) => ({
          ...variant,
          vetoed: true,
          sku: row.sku,
          title: row.title,
          image_url: row.image_url ?? null,
          cat: row.cat,
          season: row.season ?? null,
        })))),
  } : list;

  const stillVetoed = (sku: string) => !state.restored[planVetoKey(sku)];
  const stillSkipped = (row: RestockV2ExcludedMove) => !state.restored[excludedMoveKey(row)];
  const excluded: RestockV2ExcludedLists = {
    plan: excludedStyles(plan).filter((row) => stillVetoed(row.sku) && !vetoed[row.sku]).concat(hiddenStyles),
    today: excludedMoves(plan).filter(stillSkipped).concat(hiddenMoves),
  };

  return {
    ...plan,
    ...(patchedList ? { list: patchedList } : {}),
    meta: { ...plan.meta, excluded_counts: { plan: excluded.plan.length, today: excluded.today.length } },
    today,
    excluded,
    racks: {
      ...plan.racks,
      fixtures: (plan.racks?.fixtures || []).map((fixture) => ({
        ...fixture,
        keep: (fixture.keep || []).filter((item) => !vetoed[item.sku]),
        add: (fixture.add || []).filter((item) => !vetoed[item.sku]),
        // `remove` rows survive a colour veto on purpose: a vetoed colour that is physically on the
        // rail still has to come off, and hiding it would strand stock he paid for.
        remove: (fixture.remove || []).filter((item) => !vetoed[item.sku]),
        // Filter on the SERVED state, then apply his in-flight 留著／今天不做 — the other order
        // would let a 留著 flip a tile out of 'remove' and so out of the veto carve-out above.
        ...(fixture.tiles
          ? {
            tiles: fixture.tiles.filter((tile) => !vetoed[tile.sku]
              && ((tile.state || 'ok') === 'remove' || !variantGone(tile.sku, tile.variant_id)))
              .map(decided),
          }
          : {}),
      })),
    },
  };
}
